import type { ArtworkTint } from "@pax/api-contract/presets/v1alpha1";
import { cn, ground, tintProperties } from "@pax/ui";
import {
  type CSSProperties,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export interface TintCrossfadeHandle {
  show: (tint: ArtworkTint | null | undefined) => void;
}

interface Layer {
  id: number;
  tint: ArtworkTint | null;
}

export const TintCrossfade = forwardRef<
  TintCrossfadeHandle,
  {
    initialTint?: ArtworkTint | null;
    mode: "page" | "solid";
    strength?: number;
    className?: string;
  }
>(function TintCrossfade({ initialTint = null, mode, strength = 100, className }, ref) {
  const nextId = useRef(1);
  const currentTint = useRef<ArtworkTint | null>(initialTint);
  const cleanup = useRef(0);
  const [layers, setLayers] = useState<Layer[]>([{ id: 0, tint: initialTint }]);
  const [visible, setVisible] = useState(0);

  useImperativeHandle(
    ref,
    () => ({
      show(tint) {
        const nextTint = tint ?? null;
        if (sameTint(currentTint.current, nextTint)) return;
        currentTint.current = nextTint;
        const id = nextId.current++;
        window.clearTimeout(cleanup.current);
        setLayers(previous => [previous.at(-1) ?? { id: 0, tint: null }, { id, tint: nextTint }]);
        requestAnimationFrame(() => requestAnimationFrame(() => setVisible(id)));
        cleanup.current = window.setTimeout(() => setLayers([{ id, tint: nextTint }]), 750);
      },
    }),
    [],
  );

  useEffect(() => () => window.clearTimeout(cleanup.current), []);

  return (
    <div aria-hidden className={cn("pointer-events-none", className)}>
      {layers.map(layer => (
        <div
          key={layer.id}
          className={cn(
            "absolute inset-0 transition-opacity duration-700 ease-in-out",
            layer.id === visible ? "opacity-100" : "opacity-0",
          )}
          style={layerStyle(layer.tint, mode, strength)}
        />
      ))}
    </div>
  );
});

function layerStyle(
  tint: ArtworkTint | null,
  mode: "page" | "solid",
  strength: number,
): CSSProperties {
  const properties = tintProperties(tint);
  if (mode === "solid") {
    return { ...properties, backgroundColor: ground(tint ? strength : 0) };
  }
  return {
    ...properties,
    backgroundImage: `linear-gradient(to bottom,
      ${ground(100)} 0rem,
      ${ground(100)} var(--art-foot),
      ${ground(84)} 50rem,
      ${ground(72)} 100rem)`,
  };
}

function sameTint(left: ArtworkTint | null, right: ArtworkTint | null) {
  return left?.hue === right?.hue && left?.chroma === right?.chroma;
}
